'use client';

import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { useGroupedSelection } from './useGroupedSelection';

export type CheckboxOption = {
  id: string;
  label: string;
  shortLabel?: string;
  checked: boolean;
  disabled?: boolean;
};

export type CheckboxGroup = {
  title?: string;
  options: CheckboxOption[];
  requireSelection?: boolean;
};

export type CheckboxDropdownProps = {
  triggerText?: string | React.ReactNode;
  triggerClassName?: string;
  triggerSize?: 'sm' | 'default' | 'icon' | 'xs' | 'lg' | null | undefined;
  groups: CheckboxGroup[];
  triggerVariant?: any;
  width?: string;
  requireSelection?: boolean;
  placementSide?: 'top' | 'bottom' | 'left' | 'right';
  placementAlign?: 'start' | 'center' | 'end';
  placementOffset?: number;
  isDisabled?: boolean;
  dataTestid?: string;
  onChange?: (optionId: string, checked: boolean) => void;
  onSelectionChange?: (selected: Record<string, CheckboxOption[]>) => void;
};

export function CheckboxDropdown({
  triggerText = 'Select Options',
  triggerClassName = '',
  groups,
  width = 'w-56',
  triggerSize = 'sm',
  placementSide,
  placementAlign,
  requireSelection = false,
  triggerVariant = 'outline',
  isDisabled = false,
  dataTestid,

  onChange,
  onSelectionChange,
}: CheckboxDropdownProps) {
  const [open, setOpen] = React.useState(false);

  const { options, handleCheckedChange } = useGroupedSelection({
    groups,
    requireSelection,
    onChange,
    onSelectionChange,
    selectionMode: 'multiple',
  });

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        asChild
        className={open && triggerVariant === 'ghost' ? 'bg-[#01152B29]' : ''}
        disabled={isDisabled}
      >
        <Button
          size={triggerSize}
          variant={triggerVariant}
          className={cn('flex items-center justify-between gap-2', triggerClassName)}
          {...(dataTestid ? { 'data-testid': dataTestid } : {})}
        >
          <span className="text-sm">{triggerText}</span>
          <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className={cn(width, 'max-h-[65vh] overflow-y-auto scrollbar-thin')} align={placementAlign}>
        {groups.map((group, groupIndex) => {
          const isGroupRestricted = group.requireSelection || requireSelection;
          const checkedCount = group.options.reduce((count, option) => {
            return options[option.id]?.checked ? count + 1 : count;
          }, 0);

          return (
            <React.Fragment key={groupIndex}>
              {group.title && (
                <>
                  <DropdownMenuLabel>
                    {group.title}
                    {isGroupRestricted && (
                      <span className="text-xs text-muted-foreground ml-2">At least one required</span>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}

              {group.options.map((option) => {
                const currentOption = options[option.id];
                if (!currentOption) return null;

                const isLastCheckedItem = isGroupRestricted && currentOption.checked && checkedCount === 1;

                return (
                  <DropdownMenuItem
                    key={option.id}
                    onSelect={(e) => e.preventDefault()}
                    className="flex items-center gap-2"
                    data-testid={`menu_item_${generateTestId(option.label)}`}
                  >
                    <Checkbox
                      id={option.id}
                      checked={currentOption.checked}
                      disabled={currentOption.disabled || isLastCheckedItem}
                      onCheckedChange={(checked) => handleCheckedChange(option.id, checked === true)}
                      className={cn(isLastCheckedItem && 'opacity-70')}
                    />
                    <label
                      htmlFor={option.id}
                      className={cn(
                        'flex-1 cursor-pointer text-sm',
                        currentOption.disabled ? 'text-muted-foreground' : '',
                        isLastCheckedItem && 'text-muted-foreground'
                      )}
                      title={isLastCheckedItem ? 'At least one option must be selected' : ''}
                    >
                      {option.label}
                      {isLastCheckedItem && <span className="text-xs text-muted-foreground ml-2">(required)</span>}
                    </label>
                  </DropdownMenuItem>
                );
              })}

              {groupIndex < groups.length - 1 && <DropdownMenuSeparator />}
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
